/**
 * Deeply compares two values for equality.
 *
 * This function performs a deep comparison between two values to determine if they are equivalent.
 * It handles primitive types, objects, arrays, dates, regular expressions, maps, and sets.
 *
 * @param value1 - The first value to compare
 * @param value2 - The second value to compare
 * @param ignoreArrayOrder - If true, array order doesn't matter (["a", "b"] equals ["b", "a"]). Default: false
 * @returns A boolean indicating whether the values are equal
 *
 * @example
 * isEqual(1, 1) // true
 * isEqual({ a: 1 }, { a: 1 }) // true
 * isEqual([1, 2], [1, 2]) // true
 * isEqual([1, 2], [2, 1]) // false
 * isEqual([1, 2], [2, 1], true) // true (ignores order)
 * isEqual(new Date('2023-01-01'), new Date('2023-01-01')) // true
 * isEqual({ a: 1 }, { a: 2 }) // false
 */
export const isEqual = (
  value1: unknown,
  value2: unknown,
  ignoreArrayOrder = false,
): boolean => {
  // Check for identical references or both being null/undefined
  if (value1 === value2) {
    return true
  }

  // Check if both values are objects and not null
  if (
    typeof value1 === 'object' &&
    value1 !== null &&
    typeof value2 === 'object' &&
    value2 !== null
  ) {
    // Handle Date objects
    if (value1 instanceof Date) {
      return value2 instanceof Date && value1.getTime() === value2.getTime()
    }

    // Handle Regular Expressions
    if (value1 instanceof RegExp) {
      return Boolean(
        value2 instanceof RegExp &&
        value1.source === value2.source &&
        value1.flags === value2.flags,
      )
    }

    // Handle Maps
    if (value1 instanceof Map && value2 instanceof Map) {
      if (value1.size !== value2.size) {
        return false
      }

      const hasMismatch = [...value1].some(
        ([key, val]) =>
          !value2.has(key) || !isEqual(val, value2.get(key), ignoreArrayOrder),
      )
      return !hasMismatch
    }

    // Handle Sets
    if (value1 instanceof Set && value2 instanceof Set) {
      if (value1.size !== value2.size) {
        return false
      }

      const hasMismatch = [...value1].some(
        (val) =>
          ![...value2].some((val2) => isEqual(val, val2, ignoreArrayOrder)),
      )
      return !hasMismatch
    }

    // Handle Arrays
    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (value1.length !== value2.length) {
        return false
      }

      if (ignoreArrayOrder) {
        // Match each element in value1 to an unmatched element in value2
        const used = new Array<boolean>(value2.length).fill(false)
        for (const item of value1) {
          const matchIndex = value2.findIndex(
            (val, idx) => !used[idx] && isEqual(item, val, ignoreArrayOrder),
          )
          if (matchIndex === -1) {
            return false
          }
          used[matchIndex] = true
        }
        return true
      }

      const hasMismatch = value1.some(
        (item, index) => !isEqual(item, value2[index], ignoreArrayOrder),
      )
      return !hasMismatch
    }

    // Handle Plain Objects
    const obj1 = value1 as Record<string, unknown>
    const obj2 = value2 as Record<string, unknown>

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) {
      return false
    }

    // Compare all keys and values without caring about order
    return keys1.every(
      (key) =>
        keys2.includes(key) && isEqual(obj1[key], obj2[key], ignoreArrayOrder),
    )
  }

  // If we reach here, values are of different types or one is null
  return false
}
